/**
 * Slot-model price maths for the public pages (Phase 5c). Pure functions, integer cents.
 *
 * Mirrors backend/services/slot_billing.py so the page and checkout agree:
 * - multi-venue discount 10% at 2-3 venues, 20% at 4+ (coupon_for_venues);
 * - quarterly billing only (coupon_for_interval; owner decision 2026-09-27, "No discount on
 *   annual"): annual prepay already has its months free and the two never stack. Venue
 *   Sponsor is always billed quarterly, so it keeps the discount. Venues are still counted
 *   across every paid slot, whatever its tier or billing;
 * - the discount on an amount is rounded half-up to the cent: (amount * pct + 50) // 100.
 * Tier rows are GET /api/slots/tiers items (backend/api/schemas/slots.py TierCatalogItem):
 * tier_key, name, monthly_price_cents, quarterly_price_cents (may be null),
 * annual_price_cents, slots_per_venue (null = unlimited), coasters_per_venue, features,
 * badge_text, sort_order.
 */

/** Display order on the pricing page: free first, then up the ladder. */
export const SLOT_TIER_ORDER = ['free_listing', 'standard', 'premium', 'sponsor'];
export const PAID_SLOT_TIERS = ['standard', 'premium', 'sponsor'];

const toCents = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * "$1,347" for 134700, "$1,212.30" for 121230 (cents shown only when not zero),
 * "$0" for 0. Integer maths only; negative amounts keep their sign.
 */
export function formatCents(cents) {
  const c = toCents(cents);
  if (c === null) return '';
  const sign = c < 0 ? '-' : '';
  const abs = Math.abs(c);
  const dollars = Math.floor(abs / 100);
  const rem = abs % 100;
  const whole = dollars.toLocaleString('en-US');
  return `${sign}$${whole}${rem ? `.${String(rem).padStart(2, '0')}` : ''}`;
}

/** Whole number with thousands separators ("4,000"), '' when not a number. */
export function formatCount(n) {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  return Number.isFinite(v) ? Math.round(v).toLocaleString('en-US') : '';
}

/** Multi-venue discount percentage for n distinct venues: 0, 10 (2-3) or 20 (4+). */
export function multiVenueDiscountPct(venues) {
  const n = Math.trunc(Number(venues) || 0);
  if (n >= 4) return 20;
  if (n >= 2) return 10;
  return 0;
}

/** Billing intervals the multi-venue discount applies to (slot_billing.DISCOUNTED_INTERVALS). */
export const DISCOUNTED_INTERVALS = ['quarterly'];

/**
 * True when a slot billed at `interval` gets the multi-venue discount (slot_billing.discount_applies
 * on the effective interval): quarterly yes, annual never. Venue Sponsor is always billed
 * quarterly (slot_billing.effective_interval), so with tierKey 'sponsor' it always applies.
 */
export function multiVenueDiscountApplies(interval, tierKey) {
  const eff = tierKey === 'sponsor' ? 'quarterly' : interval;
  return DISCOUNTED_INTERVALS.includes(eff);
}

/**
 * Discount percentage for a slot billed at `interval` with n distinct venues
 * (slot_billing.coupon_for_interval): multiVenueDiscountPct(n) when the discount applies to that
 * interval, else 0.
 */
export function multiVenueDiscountPctFor(venues, interval, tierKey) {
  return multiVenueDiscountApplies(interval, tierKey) ? multiVenueDiscountPct(venues) : 0;
}

/** Half-up rounded percentage of an integer cent amount (slot_billing.discount_cents). */
export function discountCents(amountCents, pct) {
  const a = toCents(amountCents) || 0;
  const p = Math.trunc(Number(pct) || 0);
  return Math.floor((a * p + 50) / 100);
}

/**
 * Price of `venues` slots of one tier at `unitCents` each, after the multi-venue discount for
 * billing at `interval` (default quarterly: the monthly and quarterly figures of quarterly
 * billing; 'annual' gets no discount unless tierKey is 'sponsor', always billed quarterly).
 * Returns { venues, subtotal, pct, discount, total } in integer cents.
 */
export function multiVenuePrice(unitCents, venues, interval = 'quarterly', tierKey = undefined) {
  const n = Math.max(0, Math.trunc(Number(venues) || 0));
  const subtotal = (toCents(unitCents) || 0) * n;
  const pct = multiVenueDiscountPctFor(n, interval, tierKey);
  const discount = discountCents(subtotal, pct);
  return { venues: n, subtotal, pct, discount, total: subtotal - discount };
}

/** Quarterly price in cents: the API's quarterly_price_cents, else 3 x monthly. */
export function quarterlyCents(tier) {
  if (!tier) return 0;
  const raw = tier.quarterly_price_cents;
  const q = raw === null || raw === undefined || raw === '' ? null : toCents(raw);
  return q !== null ? q : (toCents(tier.monthly_price_cents) || 0) * 3;
}

/** Whole months an annual prepay saves against 12 x monthly (0 when none). */
export function annualMonthsFree(tier) {
  const monthly = toCents(tier && tier.monthly_price_cents) || 0;
  const annual = toCents(tier && tier.annual_price_cents);
  if (!monthly || annual === null || annual <= 0) return 0;
  const saved = monthly * 12 - annual;
  return saved > 0 ? Math.round(saved / monthly) : 0;
}

/** Monthly equivalent of a total over `months`, rounded to the cent. */
export function perMonthCents(totalCents, months) {
  const m = Math.trunc(Number(months) || 0);
  if (m <= 0) return 0;
  return Math.round((toCents(totalCents) || 0) / m);
}

/**
 * Normalise the GET /slots/tiers body: accepts the list itself or { tiers: [...] }; keeps
 * one row per known tier key, in SLOT_TIER_ORDER.
 */
export function normaliseSlotTiers(body) {
  const list = Array.isArray(body) ? body : Array.isArray(body && body.tiers) ? body.tiers : [];
  const byKey = new Map();
  list.forEach((t) => {
    if (t && SLOT_TIER_ORDER.includes(t.tier_key) && !byKey.has(t.tier_key)) byKey.set(t.tier_key, t);
  });
  return SLOT_TIER_ORDER.filter((k) => byKey.has(k)).map((k) => byKey.get(k));
}

/**
 * The published list prices (Tappd business plan, locked 2026-09-24), used only when the
 * API has no slot tiers yet (empty state) or cannot be reached, so the page never shows
 * an empty price list. Same field names as GET /slots/tiers.
 */
export const PUBLISHED_SLOT_TIERS = [
  { tier_key: 'free_listing', name: 'Free Listing', monthly_price_cents: 0, quarterly_price_cents: 0, annual_price_cents: 0, slots_per_venue: null, coasters_per_venue: 0 },
  { tier_key: 'standard', name: 'Standard', monthly_price_cents: 44900, quarterly_price_cents: 134700, annual_price_cents: 449000, slots_per_venue: 8, coasters_per_venue: 4000 },
  { tier_key: 'premium', name: 'Premium', monthly_price_cents: 89900, quarterly_price_cents: 269700, annual_price_cents: 899000, slots_per_venue: 4, coasters_per_venue: 1000 },
  { tier_key: 'sponsor', name: 'Venue Sponsor', monthly_price_cents: 199900, quarterly_price_cents: 599700, annual_price_cents: 2398800, slots_per_venue: 1, coasters_per_venue: 2000 },
];
